import type { FC } from 'react';
import * as React from 'react';
import { styled } from '../../../../styles';
import { useIntlBds } from '../../../../translations/BdsIntlProvider';
import { Chip } from '../../../Chip/Chip';
import { Grow } from '../../../Grow';
import { Typography } from '../../../Typography/Typography';
import { SelectV2Types } from '../../SelectV2.types';
import { RichTextPresenter } from './RichTextPresenter';

export const Chips: FC<SelectV2Types.SelectChipsProps> = ({
  selectedOptions,
  visibleElementsWhenSelected,
  isFocused,
  onClick,
  onWrapperClick,
  onWrapperBlur,
  onWrapperFocus,
  onFocus,
  limit: limitProps,
  maxWidth,
  loading,
  disabled,
  onDelete
}) => {
  const limit = isFocused && !limitProps?.permanent ? 999 : (limitProps?.count ?? 999);
  const { intl, format } = useIntlBds();
  const remaining = selectedOptions.length - limit;
  const endTextRaw =
    remaining <= 0
      ? undefined
      : limitProps?.text
        ? limitProps?.text
        : format(intl.Select.Chips.endText, { endText: remaining });

  const endText = endTextRaw?.replace(/({.*})/, `${remaining}`);

  return (
    <StyledDiv
      onClick={onWrapperClick}
      onBlur={onWrapperBlur}
      onBlurCapture={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          e.stopPropagation();
        }
      }}
      onFocus={onWrapperFocus}
    >
      {selectedOptions.slice(0, limit).map((currentOption, index) => {
        const hasIcon = !!visibleElementsWhenSelected?.icon && !!currentOption.icon;
        return (
          <Grow key={currentOption?.label + JSON.stringify(currentOption.value) || index} in>
            <StyledChip
              $hasIcon={hasIcon}
              $maxWidth={maxWidth}
              key={currentOption?.label || index}
              onFocus={onFocus}
              label={
                <RichTextPresenter
                  selectedOption={currentOption}
                  visibleElementsWhenSelected={visibleElementsWhenSelected}
                  variant="chip"
                  style={{ height: 24 }}
                  loading={loading}
                />
              }
              color="default"
              onClick={!disabled && onClick ? onClick : undefined}
              slotProps={{
                deleteIcon: {
                  // @ts-expect-error - need to add data-block-event to detect clicks on delete icon
                  'data-block-event': true,
                  'data-delete-option': index,
                  tabIndex: 0
                }
              }}
              tabIndex={-1}
              onDelete={
                !disabled
                  ? () => {
                      return onDelete?.(currentOption);
                    }
                  : undefined
              }
              size="small"
            />
          </Grow>
        );
      })}
      {!!endText && <StyledTypography>{endText}</StyledTypography>}
    </StyledDiv>
  );
};
const StyledDiv = styled('div')`
  min-height: 38px;
  padding-top: 6px;  
  padding-bottom: 2px;  
  max-height: 150px;
  max-width: 100%;  
  overflow-x: hidden;
  overflow-y: auto;
  display: inline-block;
  white-space: break-spaces;
`;

const StyledTypography = styled(Typography)`
  color: ${({ theme }) => theme.tokens.colorOnSurfaceVariant};
  white-space: nowrap;
  display: inline-flex;
  margin-left: 4px;  

  .body1 {
    font-size: 13px;
    line-height: 23px;
  }
`;

const StyledChip = styled(Chip)<{ $hasIcon: boolean; $maxWidth?: number }>`
    margin-top: 1px;
    margin-right: 8px;
    max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '100%')};
  
    & > .MuiChip-label {
        padding-left: ${({ $hasIcon }) => ($hasIcon ? `0px` : undefined)};
    }
`;
